import { Validators } from "@angular/forms";
import { SurveyQuestion } from "./models/survey";
import { FormService } from "./form.service";
import { inject, Injectable } from "@angular/core";
import { AnswerObj, AnswerValueType } from "./answer";

Injectable();
export class QuestionService {

    formService = inject(FormService);

    questions: SurveyQuestion[] = [];
    currentQuestion: number = 0;
    nextDisabled = false;
    error?: string;

    private _rules: QuestionRule[] = [];

    /**
     * Moves to the next question (or skips if applicable) in the survey by incrementing the survey id. Additionally,
     * checks to see if the response of the current question impacts the next downstream question.
     */
    next() {

        // Do any logic with the *submitting* question here before proceeding further.
        try {

            // Make sure if the user selects 'Other' that the textbox has some value in it, do not proceed if they leave it blank.
            this._otherValidated(SurveyNavigationDirection.Next);
        }
        catch (e) {
            this.error = (e as Error).message;
            return;
        }

        // Change to next question.
        this.currentQuestion += 1;

        // Do any logic with the *new/next* question here.

        // See if answer has triggered any rules.
        this._processRules();

        if (this.questions?.[this.currentQuestion].show == false) {

            this._skip(SurveyNavigationDirection.Next);
        }

        // Hides or shows other text box based on the value. 
        if (this._hasJsonResponse(this.questions?.[this.currentQuestion])) {

            // Stringified JSON because that is how it is stored in the database for questions with multiple response types.
            const jsonString = this.formService.forms?.[this.currentQuestion + 1].value;

            if (!jsonString) {

                // Stringified JSON is "", meaning the question was probably hidden before. Add blank Answer obj to form.
                this.formService.forms?.[this.currentQuestion + 1].setValue(JSON.stringify(new AnswerObj));
            }

            if (this._isOtherBlank(this.questions?.[this.currentQuestion])) {

                if (this._isOtherChecked(this.questions?.[this.currentQuestion])) {

                    this.formService.showOtherTextbox();
                }
                else {
                    this.formService.hideOtherTextbox();
                }
            }
            else {
                this.formService.showOtherTextbox();
            }
        }
    }

    /**
     * Moves to the previous question (or skips if applicable) in the survey by incrementing the survey id. Additionally,
     * checks to see if the response of the current question impacts the next downstream question. 
     */
    prev() {

        // Do any logic with the *current/submitting* question here before proceeding further.

        // Reset.
        this.error = undefined;
        this.nextDisabled = false;

        this._otherValidated(SurveyNavigationDirection.Previous);

        this.currentQuestion -= 1;

        // Do any logic with the *new/prev* question here.

        // See if answer has triggered any rules.
        this._processRules();

        if (this.questions?.[this.currentQuestion].show == false) {

            this._skip(SurveyNavigationDirection.Previous);
        }

        // Hides or shows other text box based on the value.
        if (this._hasJsonResponse(this.questions?.[this.currentQuestion])) {

            if (JSON.parse(this.formService.forms?.[this.currentQuestion + 1].value).other == '') {

                this.formService.hideOtherTextbox();
            }
            else {
                this.formService.showOtherTextbox();
            }
        }
    }

    /**
     * Loads the questions into the service property. Furthermore, loads all rules for questions and checks
     * to see if any previous values should trigger any rules.
     * @param questions survey questions.
     */
    loadQuestions(questions: SurveyQuestion[]) {

        // Set the questions
        this.questions = questions;

        // Load the rules
        this._loadRules();

        // Process the rules
        this._processRules();
    }

    /**
     * Returns the index of the given choice from the provided array. Only works with Decline or Other.
     * @param question survey question.
     * @param answerType AnswerType enum.
     * @returns index of the item. 
     */
    getIndex(array: any[], answerType: AnswerValueType): number {

        let index = -1;

        // Make sure we are only checking Decline or Other options
        if (answerType === AnswerValueType.Decline || answerType === AnswerValueType.Other) {

            // Map all question choices to another array, transforming to lower case for easier compare
            let choices = array?.map(item => item.toLocaleLowerCase());

            if (choices) {

                // Get the index of the value in question
                const choiceIndex = choices?.indexOf(answerType.toLowerCase());

                if (choiceIndex !== -1) {

                    // Have a valid index
                    return choiceIndex;
                }
            }
        }
        return index;
    }

    /**
     * Hides the question by modifying the show property. Furthermore, remove any form validation
     * for that question since the user won't be seeing the question.
     * @param q survey question.
     */
    private _hide(q: SurveyQuestion) {

        // UI binds to this value
        q.show = false;

        this.formService.forms?.[q.id].setValue("");

        // If question is hidden, suppress validation as an answer won't be submitted
        this.formService.forms?.[q.id].removeValidators([Validators.required]);
        this.formService.forms?.[q.id].updateValueAndValidity();

    }

    /**
     * Checks to see if the given question stores the response in the database as JSON string. Only questions that
     * have the attribute of allow_multiple or allow_other will be stored as a JSON string.
     * @param q 
     * @returns 
     */
    private _hasJsonResponse(q: SurveyQuestion): boolean {

        if (q.allow_multiple || q.allow_other) {
            return true;
        }
        else {
            return false;
        }

    }

    /**
     * Check form to see if response 'Other' has a non-empty value.
     * @param q 
     * @returns 
     */
    private _isOtherBlank(q: SurveyQuestion): boolean {

        const otherText = JSON.parse(this.formService.forms?.[q.id].value).other;

        if (otherText) {
            return false;
        }
        else {
            return true;
        }
    }

    /**
     * Check form to see if response includes 'Other' value. If so, the value is checked.
     * @param q 
     * @returns 
     */
    private _isOtherChecked(q: SurveyQuestion): boolean {

        const answers = JSON.parse(this.formService.forms?.[q.id].value).answers;

        let isChecked = false;

        for (let i = 0; i < answers.length; i++) {
            if (answers[i] == 'Other') {
                isChecked = true;
                return isChecked;
            }
        }

        return isChecked;
    }

    /**
     * Checks to see if questions have the 'hideIf' tag as stringified JSON. If so, maps to a 
     * JSON object and adds to an array that will be checked later.
     */
    private _loadRules() {

        // Loop through the questions and check for any custom rules
        this.questions.forEach(question => {

            const hideIfJson = JSON.parse(question.data!);

            if ("hideIf" in hideIfJson) {

                // hideIf json format "hideIf": [4, "ValueToTriggerOn"]
                let questionToCheck = hideIfJson.hideIf[0];
                let triggerAnswer = hideIfJson.hideIf[1];

                let hiddenQuestion: QuestionRule = {
                    "questionToHide": question.id,
                    "questionToCheck": questionToCheck,
                    "valueToCheck": triggerAnswer
                }

                this._rules?.push(hiddenQuestion);
            }
        })
    }

    /**
     * If the users selects 'Other' check/radio box, make sure they fill out the textbox before clicking next. If not, throw an error
     * and remind the user they must fill out the textbox before proceeding.
     * @returns 
     */
    private _otherValidated(dir: SurveyNavigationDirection) {

        let isOtherChecked = false;
        let hasJsonResponse = this._hasJsonResponse(this.questions?.[this.currentQuestion]);

        if (hasJsonResponse) {
            isOtherChecked = this._isOtherChecked(this.questions?.[this.currentQuestion]);
        }
        else {
            return;
        }

        if (isOtherChecked) {

            // Action will be different depending on whether navigating to next or previous question.
            if (dir == SurveyNavigationDirection.Next) {

                // Show the box
                this.formService.showOtherTextbox();

                // Make sure it's not blank!
                const otherText = JSON.parse(this.formService.forms?.[this.currentQuestion + 1].value).other;

                if (!otherText) {
                    this.nextDisabled = true;
                    throw new Error("Other textbox cannot be blank!");
                }
            }
            else if (dir == SurveyNavigationDirection.Previous) {

                // If previous, uncheck 'Other', clear and hide the textbox.
                this.formService.hideOtherTextbox();
            }
        }
        else {
            return;
        }
    }

    /**
     * Loop through visibility rules detected in _loadRules() and see if any of the rules criteria are met. If
     * any rules are met, show/hide the question.
     */
    private _processRules() {

        if (this._rules) {

            // Loop through rules and see if any are matching
            this._rules.forEach(rule => {

                // Parse the rule, adding variables for code readability
                let valueToCheck = rule.valueToCheck; // "No"
                let questionToCheck = rule.questionToCheck; // 4
                let questionToHide = rule.questionToHide; // 5

                // If the threshold is met, hide the question
                if (this.formService.forms?.[questionToCheck].value == valueToCheck) { // Updated to new form service

                    this.questions?.forEach(q => {
                        if (q.id == questionToHide) {

                            // Hide the question
                            this._hide(q);
                        }
                    })
                }
                // If the threshold is not met, show the question
                else {

                    this.questions?.forEach(q => {
                        if (q.id == questionToHide) {

                            // Show the question
                            this._show(q);
                        }
                    })
                }
            })
        }
    }

    /**
     * Shows the question by modifying the show property. Furthermore, adds any form validation
     * for that question since the user will be filling out this question.
     * @param q survey question.
     */
    private _show(q: SurveyQuestion) {

        // UI binds to this value
        q.show = true;

        // If question is not hidden, enforce validation
        this.formService.forms?.[q.id].addValidators([Validators.required]);
        this.formService.forms?.[q.id].updateValueAndValidity();
    }

    /**
     * Update the current question ID (+1/-1), affecting which question is displayed in the UI. Called when a question 
     * should be hidden and therefore skipped when the user is navigating through the questions.  
     * @param navKey 
     */
    private _skip(direction: SurveyNavigationDirection) {

        if (direction == 0) {

            //Previous
            this.currentQuestion -= 1;
        }
        else if (direction == 1) {

            // Next
            this.currentQuestion += 1;
        }
    }
}

export enum SurveyNavigationDirection {
    Previous,
    Next,
}

export type QuestionRule = {
    questionToHide: number;
    questionToCheck: number;
    valueToCheck: string;
}